// NPM packages
import React from 'react';
import { observer } from 'mobx-react-lite';
import * as yup from 'yup';

// All other imports
import TextField from 'components/core/TextField';
import TierSelector from 'components/pages/UserMemberships/TierSelector';
import { useServices } from 'services';
import SignUpData from 'types/SignUpData';
import SignUpSuccess from 'components/auth/SignUpSuccess';
import SignUpError from 'components/auth/SignUpError';
import Form from 'components/core/Form';
import AffiliationAutocomplete from 'components/core/AffiliationAutocomplete';

export interface SignUpComponentProps {
  className?: string;
}

const ORCID_ID_REGEX = /^(\d{4}-){3}\d{3}[\dX]$/;
const ORCID_FORMAT_MSG =
  "ORCID iD must be in the format XXXX-XXXX-XXXX-XXXX (e.g. 0000-1234-5678-9101).";

const formSchema: yup.ObjectSchema<SignUpData> = yup.object({
  email: yup
    .string()
    .required()
    .email()
    .label('Email'),
  firstName: yup.string().required().label('First Name'),
  lastName: yup.string().required().label('Last Name'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters.')
    .matches(
      /^.*(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^\w]|[&.-]).*$/,
      'Password must have uppercase, lowercase, symbol, and numeric characters.'
    )
    .label('Password'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required()
    .label('Confirm Password'),
  affiliation: yup
    .string()
    .label('Affiliation')
    .optional(),
  justification: yup.string().required().label('Justification'),
  membershipTier: yup
    .string()
    .oneOf(['Tier 1', 'Tier 2', 'Tier 3'] as const)
    .required()
    .label('Membership Tier'),
  orcidId: yup
    .string()
    .trim()
    .when('membershipTier', {
      is: 'Tier 3',
      then: (schema) =>
        schema
          .required(ORCID_FORMAT_MSG)
          .min(1, ORCID_FORMAT_MSG)
          .matches(
            ORCID_ID_REGEX,
            ORCID_FORMAT_MSG
          ),
      otherwise: (schema) =>
        schema.matches(ORCID_ID_REGEX, {
          message: ORCID_FORMAT_MSG,
          excludeEmptyString: true,
        }),
    })
    .label('ORCID ID'),
});

const defaultFormValues: SignUpData = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  confirmPassword: '',
  affiliation: '',
  justification: '',
  membershipTier: 'Tier 1',
  orcidId: '',
};

function SignUpComponent(
  props: SignUpComponentProps
): React.ReactElement | null {
  const { className } = props;
  const { authService } = useServices();

  return (
    <Form<SignUpData>
      className={className}
      schema={formSchema}
      defaultValues={defaultFormValues}
      onSubmit={(data) => authService.signUp(data)}
      getErrorMessage={(rejectedVal) => {
        return <SignUpError error={rejectedVal} />;
      }}
      getSuccessMessage={() => <SignUpSuccess />}
      submitText="Sign Up"
    >
      {(formState) => {
        const isT3 = formState.watch('membershipTier') === 'Tier 3';

        return (
        <>
          <TextField
            formState={formState}
            fieldName="email"
            textFieldProps={{
              type: 'email',
              onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                const lowercaseValue = e.target.value.toLowerCase();
                formState.setValue('email', lowercaseValue);
              },
            }}
          />
          <TextField formState={formState} fieldName="firstName" />
          <TextField formState={formState} fieldName="lastName" />
          <AffiliationAutocomplete
            formState={formState}
            fieldName="affiliation"
          />
          <TextField
            formState={formState}
            fieldName="orcidId"
            textFieldProps={{
              label: isT3 ? 'ORCID iD' : 'ORCID iD (optional)',
              placeholder: '0000-0002-1825-0097',
              required: isT3,
              InputLabelProps: { shrink: true, sx: { whiteSpace: 'nowrap' } },
            }}
          />
          <TextField
            formState={formState}
            fieldName="justification"
            textFieldProps={{
              multiline: true,
              rows: 4,
              label: 'Why are you interested in MSD-LIVE?',
            }}
          />
          <TierSelector
            formState={formState}
            fieldName="membershipTier"
          />
          <TextField
            formState={formState}
            fieldName="password"
            textFieldProps={{ type: 'password' }}
          />
          <TextField
            formState={formState}
            fieldName="confirmPassword"
            textFieldProps={{ type: 'password' }}
          />
        </>
        );
      }}
    </Form>
  );
}

export default observer(SignUpComponent);
